import { useState, useEffect } from 'react'
import Navbar from '../../../../components/navbar/Navbar'
import { db, auth } from '../../../../firebase/firebase'
import { updateDoc, doc, onSnapshot } from "firebase/firestore"
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage"
import avatar from "../../../../assets/maleAvatar.webp"
import styled from './StudentProfileEditor.module.css'
import { IoIosCloudUpload } from 'react-icons/io'
import { useDispatch } from 'react-redux'
import { setStudentProfileImage, setStudentData } from '../../../../store'
import { useSelector } from 'react-redux' 
import { useTranslation } from 'react-i18next'

const StudentProfileEditor = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [studentInfo, setStudentInfo] = useState({
    firstName: "",
    lastName: "",
    birthYear: "",
    gender: "",
    profileImage: ""
  })
  const studentProfileImage = useSelector((state) => state.student.profileImage)

  const [editing, setEditing] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  // Fetch student data
  useEffect(() => {    
    const studentDocRef = doc(db, "students", auth.currentUser.uid)
    const unsubscribe = onSnapshot(studentDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data()
        setStudentInfo(data)
      }
    })
    return () => unsubscribe() 
  }, [])

  // const handleSubmit = async (e) => {
  //   e.preventDefault()
  //   setIsUpdating(true)
 
  //   try {
  //     const studentDocRef = doc(db, "students", auth.currentUser.uid)
  //     await updateDoc(studentDocRef, studentInfo)
  //     setEditing(false)
  //   } catch (error) {
  //     console.error("Error updating document: ", error)
  //   } finally {
  //     setIsUpdating(false)
  //   }
  // }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsUpdating(true)
    try {
      await updateDoc(doc(db, "students", auth.currentUser.uid), studentInfo)
      dispatch(setStudentData(studentInfo))
      setEditing(false)
    } catch (error) {
      console.error("Error updating document: ", error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 1 * 1024 * 1024) {
      alert("Maximum image size is 1MB")
      return
    }

    setIsUpdating(true)
    try {
      const storage = getStorage()
      const storageRef = ref(storage, `studentProfileImages/${auth.currentUser.uid}/${Date.now()}_${file.name}`)
      await uploadBytes(storageRef, file)
      const url = await getDownloadURL(storageRef)

      // Delete old image if exists
      if (studentInfo.profileImage) {
        try {
          const oldImageRef = ref(storage, studentInfo.profileImage)
          await deleteObject(oldImageRef)
        } catch (error) {
        }
      }

    const updatedInfo = { ...studentInfo, profileImage: url }
    await updateDoc(doc(db, "students", auth.currentUser.uid), updatedInfo) 
    dispatch(setStudentData(updatedInfo))
    dispatch(setStudentProfileImage(url)) 
    } 
    
    catch (error) {
      console.error("Error uploading image:", error)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <>
      {!editing ? (
        <div className={styled.profileContainer}>
          <img 
            src={studentProfileImage || avatar} 
            className={styled.image} 
            alt="Profile" 
            onError={(e) => { e.target.src = avatar }}
          />
          <h3>{studentInfo.firstName}</h3>
          <h3>{studentInfo.lastName}</h3>
          <h3>
            {studentInfo?.birthYear != null &&
            studentInfo?.birthYear !== "" &&
            !Number.isNaN(Number(studentInfo.birthYear))
              ? new Date().getFullYear() - Number(studentInfo.birthYear)
              : null}
          </h3>
          <h3>{studentInfo.gender}</h3>
          <button 
            onClick={() => setEditing(true)} 
            className={styled.editProfile}
            disabled={isUpdating}
          >
            {isUpdating ? t('StudentProfileEditor.loading') : t('StudentProfileEditor.editProfile')}
          </button>
        </div>
      ) : (
        <form className={styled.form} onSubmit={handleSubmit}>
          <Navbar />
          <button 
            type="button" 
            className={styled.closeBtn} 
            onClick={() => !isUpdating && setEditing(false)}
            disabled={isUpdating}
          >
            x
          </button>

          <label className={styled.fileInputLabel}>
            <IoIosCloudUpload className={styled.uploadIcon}/> {t('StudentProfileEditor.changeProfilePicture')}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isUpdating}
              className={styled.fileInput}
            />
          </label>

          <input
            type="text"
            onChange={(e) => setStudentInfo({ ...studentInfo, firstName: e.target.value })}
            placeholder={t('StudentProfileEditor.yourName')}
            value={studentInfo.firstName}
            className={styled.inputField}
            disabled={isUpdating}
          />
          <input
            type="text"
            value={studentInfo.lastName}
            onChange={(e) => setStudentInfo({ ...studentInfo, lastName: e.target.value })}
            placeholder={t('StudentProfileEditor.yourSurname')}
            className={styled.inputField}
            disabled={isUpdating}
          />
          <input
            type="number"
            placeholder={t('StudentProfileEditor.birthYear')}
            value={studentInfo.birthYear}
            onChange={(e) => setStudentInfo({ ...studentInfo, birthYear: e.target.value })}
            className={styled.inputField}
            disabled={isUpdating}
          />
          <select
            onChange={(e) => setStudentInfo({ ...studentInfo, gender: e.target.value })}
            name="gender"
            value={studentInfo.gender}
            className={styled.selectField}
            disabled={isUpdating}
          >
            <option value="" disabled>{t('StudentProfileEditor.selectGender')}</option>
            <option value="Male">{t('StudentProfileEditor.male')}</option>
            <option value="Female">{t('StudentProfileEditor.female')}</option>
          </select>
          <button 
            type="submit" 
            className={styled.submitBtn}
            disabled={isUpdating}
          >
            {isUpdating ? t('StudentProfileEditor.saving') : t('StudentProfileEditor.submit')}
          </button>
        </form>
      )}
    </>
  )
}

export default StudentProfileEditor
